import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

import {
  ApplicantApplicationState,
  ApplicantDocumentList,
  ApplicantDraftEvidenceList,
  ApplicantEvidenceRequirement,
  ApplicantForm,
  ApplicantReadiness,
  ApplicantSubmission,
  deleteApplicantDraftEvidenceDocument,
  getApplicantReadiness,
  getApplicantProfile,
  listApplicantDraftEvidence,
  listApplicantApplicationStates,
  listApplicantForms,
  listApplicantDocuments,
  listApplicantSubmissions,
  resubmitApplicantSubmission,
  saveApplicantApplicationDraft,
  StudentProfile,
  submitApplicantApplicationDraft,
  updateApplicantApplicationDraft,
  uploadApplicantDraftEvidenceDocument,
  uploadApplicantDocument,
} from "@/api/studentApi";
import { formatWorkflowStatusLabel } from "@/utils/semanticLabels";

type ApplicantFieldSchema = NonNullable<ApplicantForm["schema"]["properties"]>[string];
type SubmissionFailureKind = "validation" | "network" | "server";

function fieldLabel(fieldName: string, schema?: ApplicantFieldSchema): string {
  return schema?.title || fieldName.replaceAll("_", " ");
}

function canonicalFieldKey(fieldName: string, schema?: ApplicantFieldSchema): string {
  return schema?.["x-canonical-field"] || schema?.["x-field-type"] || fieldName;
}

function isNumericField(fieldName: string, schema?: ApplicantFieldSchema): boolean {
  const key = canonicalFieldKey(fieldName, schema);
  return (
    schema?.type === "number" ||
    schema?.type === "integer" ||
    key === "family_income" ||
    key === "academic_score"
  );
}

function validateApplicantField(
  fieldName: string,
  schema: ApplicantFieldSchema | undefined,
  value: string,
  required: boolean
): string | null {
  const label = fieldLabel(fieldName, schema);
  const key = canonicalFieldKey(fieldName, schema);
  const trimmed = value.trim();

  if (required && !trimmed) {
    return `${label} is required.`;
  }

  if (!trimmed) {
    return null;
  }

  if (key === "aadhaar_number") {
    if (!/^\d+$/.test(trimmed)) {
      return `${label} must contain numbers only.`;
    }
    if (trimmed.length !== 12) {
      return `${label} must contain exactly 12 digits.`;
    }
  }

  if (isNumericField(fieldName, schema)) {
    if (!/^-?\d+(\.\d+)?$/.test(trimmed) || !Number.isFinite(Number(trimmed))) {
      return `${label} must be a number.`;
    }
  }

  if (schema?.minLength !== undefined && trimmed.length < schema.minLength) {
    return `${label} must be at least ${schema.minLength} characters.`;
  }

  if (schema?.maxLength !== undefined && trimmed.length > schema.maxLength) {
    return `${label} must be at most ${schema.maxLength} characters.`;
  }

  if (schema?.minimum !== undefined && Number(trimmed) < schema.minimum) {
    return `${label} must be at least ${schema.minimum}.`;
  }

  if (schema?.maximum !== undefined && Number(trimmed) > schema.maximum) {
    return `${label} must be at most ${schema.maximum}.`;
  }

  if (schema?.pattern) {
    try {
      if (!new RegExp(schema.pattern).test(trimmed)) {
        return `${label} does not match the required format.`;
      }
    } catch {
      return null;
    }
  }

  return null;
}

function normalizeApplicantPayload(
  values: Record<string, string>,
  properties: Record<string, ApplicantFieldSchema>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(values).map(([fieldName, value]) => {
      const schema = properties[fieldName];
      const trimmed = value.trim();
      if (isNumericField(fieldName, schema) && trimmed) {
        return [fieldName, Number(trimmed)];
      }
      return [fieldName, trimmed];
    })
  );
}

function backendErrorMessage(detail: unknown): string {
  if (typeof detail === "string") {
    return detail;
  }
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const payload = detail as Record<string, unknown>;
    if (typeof payload.message === "string") return payload.message;
    if (typeof payload.reason === "string") return payload.reason.replaceAll("_", " ");
  }
  return "The server rejected this submission. Your typed values are still here.";
}

function backendFieldErrors(detail: unknown): Record<string, string> {
  if (!Array.isArray(detail)) {
    return {};
  }
  return detail.reduce<Record<string, string>>((errors, item) => {
    if (!item || typeof item !== "object") return errors;
    const payload = item as { loc?: unknown[]; msg?: unknown };
    const loc = Array.isArray(payload.loc) ? payload.loc : [];
    const fieldName = [...loc].reverse().find((part) => typeof part === "string");
    if (typeof fieldName === "string" && fieldName !== "body") {
      errors[fieldName] = typeof payload.msg === "string" ? payload.msg : "Invalid value.";
    }
    return errors;
  }, {});
}

function formatSubmittedValue(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "Not provided";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

function applicantStatusLabel(status: string): string {
  return formatWorkflowStatusLabel(status);
}

function evidenceCompletionLabel(state: string): string {
  if (state === "satisfied") return "Satisfied";
  if (state === "missing_required") return "Needed";
  if (state === "unsupported_method") return "Method unavailable";
  if (state === "not_required") return "Optional";
  return state.replaceAll("_", " ");
}

function EvidenceRequirementsPanel({
  requirements,
  title = "Evidence requirements",
  editable = false,
  disabled = false,
  uploadTypeByRequirement = {},
  uploadingRequirement = null,
  onSelectDocumentType,
  onUploadDocument,
  onRemoveDocument,
}: {
  requirements: ApplicantEvidenceRequirement[];
  title?: string;
  editable?: boolean;
  disabled?: boolean;
  uploadTypeByRequirement?: Record<string, string>;
  uploadingRequirement?: string | null;
  onSelectDocumentType?: (requirementKey: string, documentType: string) => void;
  onUploadDocument?: (requirement: ApplicantEvidenceRequirement, file: File | null) => void;
  onRemoveDocument?: (requirement: ApplicantEvidenceRequirement, documentId: string) => void;
}) {
  if (requirements.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <div className="mt-3 space-y-3">
        {requirements.map((requirement) => (
          <div key={requirement.requirement_key} className="rounded border bg-white p-3 text-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-medium text-slate-950">{requirement.display_label}</p>
                {requirement.display_description ? (
                  <p className="mt-1 text-xs text-slate-600">
                    {requirement.display_description}
                  </p>
                ) : null}
              </div>
              <span
                className={`rounded px-2 py-1 text-xs font-medium ${
                  requirement.completion_state === "satisfied"
                    ? "bg-emerald-50 text-emerald-800"
                    : requirement.completion_state === "unsupported_method"
                      ? "bg-rose-50 text-rose-800"
                      : "bg-amber-50 text-amber-900"
                }`}
              >
                {evidenceCompletionLabel(requirement.completion_state)}
              </span>
            </div>
            {requirement.accepted_methods.length > 0 ? (
              <div className="mt-3">
                <p className="text-xs font-medium uppercase text-slate-500">Accepted methods</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {requirement.accepted_methods.map((method) => (
                    <span
                      key={method.method_key}
                      className={`rounded px-2 py-1 text-xs ${
                        method.supported
                          ? "bg-indigo-50 text-indigo-800"
                          : "bg-slate-100 text-slate-600"
                      }`}
                      title={method.display_description}
                    >
                      {method.display_label}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {requirement.accepted_document_types.length > 0 ? (
              <div className="mt-3">
                <p className="text-xs font-medium uppercase text-slate-500">
                  Accepted documents
                </p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {requirement.accepted_document_types.map((documentType) => (
                    <span
                      key={documentType.document_type}
                      className={`rounded px-2 py-1 text-xs ${
                        documentType.uploaded
                          ? "bg-emerald-50 text-emerald-800"
                          : "bg-white text-slate-700 ring-1 ring-slate-200"
                      }`}
                    >
                      {documentType.display_label}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {requirement.artifacts.length > 0 ? (
              <div className="mt-3 space-y-2">
                {requirement.artifacts.map((artifact) => (
                  <div
                    key={`${artifact.requirement_key}-${artifact.document_id ?? artifact.collected_at}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded bg-slate-50 px-2 py-1 text-xs text-slate-700"
                  >
                    <span>
                      {artifact.document_type
                        ? artifact.document_type.replaceAll("_", " ")
                        : artifact.method_key.replaceAll("_", " ")}
                      {" - "}
                      {artifact.collection_status}
                    </span>
                    {editable && artifact.document_id && onRemoveDocument ? (
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onRemoveDocument(requirement, artifact.document_id as string)}
                        className="rounded border border-slate-300 bg-white px-2 py-1 font-medium text-slate-800 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
            {editable ? (
              <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr]">
                <select
                  value={
                    uploadTypeByRequirement[requirement.requirement_key] ??
                    requirement.accepted_document_types[0]?.document_type ??
                    ""
                  }
                  disabled={
                    disabled ||
                    requirement.accepted_document_types.length === 0 ||
                    !requirement.accepted_methods.some(
                      (method) => method.method_key === "DOCUMENT_UPLOAD" && method.supported
                    )
                  }
                  onChange={(event) =>
                    onSelectDocumentType?.(requirement.requirement_key, event.target.value)
                  }
                  className="rounded border px-3 py-2 text-sm"
                >
                  {requirement.accepted_document_types.map((documentType) => (
                    <option key={documentType.document_type} value={documentType.document_type}>
                      {documentType.display_label}
                    </option>
                  ))}
                </select>
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  disabled={
                    disabled ||
                    uploadingRequirement === requirement.requirement_key ||
                    requirement.accepted_document_types.length === 0 ||
                    !requirement.accepted_methods.some(
                      (method) => method.method_key === "DOCUMENT_UPLOAD" && method.supported
                    )
                  }
                  onChange={(event) => {
                    onUploadDocument?.(requirement, event.currentTarget.files?.[0] ?? null);
                    event.currentTarget.value = "";
                  }}
                  className="rounded border px-3 py-2 text-sm"
                />
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function toFieldValueMap(data: Record<string, unknown> | undefined): Record<string, string> {
  return Object.fromEntries(
    Object.entries(data ?? {})
      .filter(([key]) => !key.startsWith("_") && key !== "evidence_artifacts")
      .map(([key, value]) => [
        key,
        value === null || value === undefined ? "" : String(value),
      ])
  );
}

const ApplicantDashboard: React.FC = () => {
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [readiness, setReadiness] = useState<ApplicantReadiness | null>(null);
  const [forms, setForms] = useState<ApplicantForm[]>([]);
  const [applicationStates, setApplicationStates] = useState<ApplicantApplicationState[]>([]);
  const [submissions, setSubmissions] = useState<ApplicantSubmission[]>([]);
  const [documentsBySubmission, setDocumentsBySubmission] = useState<
    Record<number, ApplicantDocumentList>
  >({});
  const [draftEvidenceByDraft, setDraftEvidenceByDraft] = useState<
    Record<number, ApplicantDraftEvidenceList>
  >({});
  const [uploadTypeBySubmission, setUploadTypeBySubmission] = useState<Record<number, string>>({});
  const [uploadTypeByRequirement, setUploadTypeByRequirement] = useState<Record<string, string>>({});
  const [uploadingDocument, setUploadingDocument] = useState<number | null>(null);
  const [uploadingDraftRequirement, setUploadingDraftRequirement] = useState<string | null>(null);
  const [formId, setFormId] = useState("");
  const [academicYear, setAcademicYear] = useState(String(new Date().getFullYear()));
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [error, setError] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [failureKind, setFailureKind] = useState<SubmissionFailureKind | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileData, formData, applicationStateData, submissionData] =
        await Promise.all([
        getApplicantProfile(),
        listApplicantForms(),
        listApplicantApplicationStates(),
        listApplicantSubmissions(),
      ]);
      const readinessData = await getApplicantReadiness();
      setProfile(profileData);
      setReadiness(readinessData);
      setForms(formData);
      setApplicationStates(applicationStateData);
      setSubmissions(submissionData);
      const draftEvidenceEntries = await Promise.all(
        applicationStateData
          .map((state) => state.draft?.id)
          .filter((draftId): draftId is number => draftId !== undefined)
          .map(async (draftId) => {
            const evidence = await listApplicantDraftEvidence(draftId);
            return [draftId, evidence] as const;
          })
      );
      setDraftEvidenceByDraft(Object.fromEntries(draftEvidenceEntries));
      const documentEntries = await Promise.all(
        submissionData.map(async (submission) => {
          const documents = await listApplicantDocuments(submission.id);
          return [submission.id, documents] as const;
        })
      );
      const documentMap = Object.fromEntries(documentEntries);
      setDocumentsBySubmission(documentMap);
      setUploadTypeBySubmission((current) => {
        const next = { ...current };
        documentEntries.forEach(([submissionId, documents]) => {
          const firstMissing = documents.requirements.find((item) => !item.uploaded);
          if (!next[submissionId] && firstMissing) {
            next[submissionId] = firstMissing.document_type;
          }
        });
        return next;
      });
      if (!formId && formData[0]) {
        setFormId(String(formData[0].form_version_id));
        setAcademicYear(String(formData[0].academic_year));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load applicant workspace.");
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const draftKey = `applicant_draft:${formId || "none"}`;
    if (Object.keys(fieldValues).length > 0) {
      sessionStorage.setItem(draftKey, JSON.stringify(fieldValues));
    }
  }, [fieldValues, formId]);

  useEffect(() => {
    const draftKey = `applicant_draft:${formId || "none"}`;
    const raw = sessionStorage.getItem(draftKey);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Record<string, string>;
      setFieldValues(parsed);
    } catch {
      sessionStorage.removeItem(draftKey);
    }
  }, [formId]);

  const saveDraft = async () => {
    setError(null);
    setSubmissionSuccess(null);
    setFailureKind(null);

    const nextFieldErrors = Object.fromEntries(
      fieldEntries.flatMap(([name, schema]) => {
        const message = validateApplicantField(
          name,
          schema,
          effectiveFieldValues[name] ?? "",
          false
        );
        return message ? [[name, message]] : [];
      })
    );

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setFailureKind("validation");
      setError("Please correct the highlighted fields before saving this draft.");
      return;
    }

    if (!selectedForm || !selectedApplicationState?.editable || resubmissionTarget) {
      setSubmissionSuccess("This application cannot be saved as a draft in its current state.");
      return;
    }

    setSavingDraft(true);
    try {
      const normalizedData = normalizeApplicantPayload(effectiveFieldValues, properties);
      const existingDraftId = selectedApplicationState.draft?.id;
      if (existingDraftId) {
        await updateApplicantApplicationDraft(existingDraftId, { data: normalizedData });
      } else {
        await saveApplicantApplicationDraft({
          form_version_id: Number(formId),
          academic_year: Number(academicYear),
          data: normalizedData,
        });
      }
      setFieldErrors({});
      setSubmissionSuccess("Draft saved. You can return later before final submission.");
      sessionStorage.removeItem(`applicant_draft:${formId || "none"}`);
      setFieldValues({});
      await load();
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        setFailureKind(status === 400 || status === 422 ? "validation" : "server");
        setError(
          status === 400 || status === 422
            ? backendErrorMessage(detail)
            : "The server could not save this draft. Your typed values are still here; retry is safe."
        );
      } else {
        setFailureKind("network");
        setError(
          err instanceof Error
            ? `${err.message}. Your typed values are still here; retry is safe.`
            : "Network failure. Your typed values are still here; retry is safe."
        );
      }
    } finally {
      setSavingDraft(false);
    }
  };

  const submitApplication = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmissionSuccess(null);
    setFailureKind(null);

    const nextFieldErrors = Object.fromEntries(
      fieldEntries.flatMap(([name, schema]) => {
        const message = validateApplicantField(
          name,
          schema,
          effectiveFieldValues[name] ?? "",
          required.has(name)
        );
        return message ? [[name, message]] : [];
      })
    );

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setFailureKind("validation");
      setError("Please correct the highlighted fields. Your typed values were not cleared.");
      return;
    }

    if (selectedApplicationState && !selectedApplicationState.editable && !resubmissionTarget) {
      setSubmissionSuccess(selectedApplicationState.cognition_message);
      return;
    }

    setSubmitting(true);

    try {
      const normalizedData = normalizeApplicantPayload(effectiveFieldValues, properties);
      if (resubmissionTarget) {
        await resubmitApplicantSubmission(resubmissionTarget.id, {
          data: normalizedData,
          reason: "Applicant submitted corrections from workspace.",
        });
      } else {
        const draft =
          selectedApplicationState?.draft ??
          (await saveApplicantApplicationDraft({
            form_version_id: Number(formId),
            academic_year: Number(academicYear),
            data: normalizedData,
          }));
        const saved = await submitApplicantApplicationDraft(draft.id);
        setSubmissionSuccess(`Application #${saved.id} was submitted and is ready for review.`);
      }
      setFieldErrors({});
      setFieldValues({});
      sessionStorage.removeItem(`applicant_draft:${formId || "none"}`);
      await load();
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        const apiFieldErrors = backendFieldErrors(detail);
        setFieldErrors(apiFieldErrors);
        setFailureKind(status === 400 || status === 422 ? "validation" : "server");
        setError(
          status === 400 || status === 422
            ? backendErrorMessage(detail)
            : "The server could not save this submission. Your typed values are still here; retry is safe."
        );
      } else {
        setFailureKind("network");
        setError(
          err instanceof Error
            ? `${err.message}. Your typed values are still here; retry is safe.`
            : "Network failure. Your typed values are still here; retry is safe."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const uploadDocument = async (submissionId: number, file: File | null) => {
    if (!file) return;
    const documentType = uploadTypeBySubmission[submissionId];
    if (!documentType) {
      setError("Select a document type before uploading.");
      return;
    }

    setError(null);
    setUploadingDocument(submissionId);
    try {
      await uploadApplicantDocument(submissionId, documentType, file);
      const documents = await listApplicantDocuments(submissionId);
      setDocumentsBySubmission((current) => ({
        ...current,
        [submissionId]: documents,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not upload document.");
    } finally {
      setUploadingDocument(null);
    }
  };

  const resubmissionTarget = submissions.find(
    (submission) => submission.status === "resubmission_required"
  );
  const selectedForm = forms.find(
    (form) =>
      resubmissionTarget
        ? Number(form.form_id) === Number(resubmissionTarget.form_id)
        : String(form.form_version_id) === String(formId)
  ) ?? null;
  const selectedApplicationState =
    applicationStates.find((state) =>
      resubmissionTarget
        ? Number(state.form.form_id) === Number(resubmissionTarget.form_id)
        : String(state.form.form_version_id) === String(formId)
    ) ?? null;
  const properties = useMemo(() => selectedForm?.schema?.properties ?? {}, [selectedForm]);
  const required = useMemo(() => new Set(selectedForm?.schema?.required ?? []), [selectedForm]);
  const fieldEntries = useMemo(() => Object.entries(properties), [properties]);
  const isFormEditable = Boolean(
    selectedForm && (resubmissionTarget || selectedApplicationState?.editable !== false)
  );
  const submittedData = selectedApplicationState?.submission?.submitted_data ?? {};
  const savedDraftValues = toFieldValueMap(selectedApplicationState?.draft?.data);
  const effectiveFieldValues = { ...savedDraftValues, ...fieldValues };
  const hasSavedDraft = Boolean(selectedApplicationState?.draft);
  const currentDraftId = selectedApplicationState?.draft?.id ?? null;
  const currentDraftEvidence = currentDraftId ? draftEvidenceByDraft[currentDraftId] : undefined;
  const evidenceRequirements =
    currentDraftEvidence?.evidence_requirements ??
    selectedApplicationState?.form.evidence_requirements ??
    selectedForm?.evidence_requirements ??
    [];
  const hasUnsavedBrowserChanges =
    isFormEditable &&
    Object.keys(fieldValues).length > 0 &&
    JSON.stringify(fieldValues) !== "{}";
  const submittedEntries = Object.entries(submittedData).filter(
    ([name]) => properties[name] !== undefined
  );
  const formHeading = resubmissionTarget
    ? "Resubmit Application"
    : selectedApplicationState?.existing_application
      ? selectedApplicationState.lifecycle_label
      : "New Application";

  const updateFieldValue = (
    fieldName: string,
    schema: ApplicantFieldSchema | undefined,
    nextValue: string
  ) => {
    setFieldValues((current) => ({
      ...current,
      [fieldName]: nextValue,
    }));
    setSubmissionSuccess(null);
    const message = validateApplicantField(
      fieldName,
      schema,
      nextValue,
      required.has(fieldName)
    );
    setFieldErrors((current) => {
      const next = { ...current };
      if (message) {
        next[fieldName] = message;
      } else {
        delete next[fieldName];
      }
      return next;
    });
  };

  const ensureDraftForEvidence = async (): Promise<number | null> => {
    if (currentDraftId) {
      return currentDraftId;
    }
    if (!selectedForm || !selectedApplicationState?.editable || resubmissionTarget) {
      setError("Save an editable draft before uploading evidence.");
      setFailureKind("validation");
      return null;
    }
    const normalizedData = normalizeApplicantPayload(effectiveFieldValues, properties);
    const draft = await saveApplicantApplicationDraft({
      form_version_id: Number(formId),
      academic_year: Number(academicYear),
      data: normalizedData,
    });
    return draft.id;
  };

  const uploadDraftEvidence = async (
    requirement: ApplicantEvidenceRequirement,
    file: File | null
  ) => {
    if (!file) return;
    const documentType =
      uploadTypeByRequirement[requirement.requirement_key] ??
      requirement.accepted_document_types[0]?.document_type;
    if (!documentType) {
      setError("Select an accepted document before uploading evidence.");
      setFailureKind("validation");
      return;
    }
    setError(null);
    setSubmissionSuccess(null);
    setUploadingDraftRequirement(requirement.requirement_key);
    try {
      const draftId = await ensureDraftForEvidence();
      if (!draftId) return;
      const result = await uploadApplicantDraftEvidenceDocument(
        draftId,
        requirement.requirement_key,
        "DOCUMENT_UPLOAD",
        documentType,
        file
      );
      const evidence = await listApplicantDraftEvidence(draftId);
      setDraftEvidenceByDraft((current) => ({
        ...current,
        [draftId]: {
          ...evidence,
          evidence_requirements: result.evidence_requirements,
        },
      }));
      setSubmissionSuccess("Evidence uploaded and saved with this draft.");
      await load();
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setFailureKind("validation");
        setError(backendErrorMessage(err.response?.data?.detail));
      } else {
        setFailureKind("network");
        setError(err instanceof Error ? err.message : "Could not upload draft evidence.");
      }
    } finally {
      setUploadingDraftRequirement(null);
    }
  };

  const removeDraftEvidence = async (
    _requirement: ApplicantEvidenceRequirement,
    documentId: string
  ) => {
    if (!currentDraftId) return;
    setError(null);
    setUploadingDraftRequirement(documentId);
    try {
      const result = await deleteApplicantDraftEvidenceDocument(currentDraftId, documentId);
      const evidence = await listApplicantDraftEvidence(currentDraftId);
      setDraftEvidenceByDraft((current) => ({
        ...current,
        [currentDraftId]: {
          ...evidence,
          complete: result.complete,
          evidence_requirements: result.evidence_requirements,
        },
      }));
      setSubmissionSuccess("Evidence removed from this draft.");
      await load();
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setFailureKind("validation");
        setError(backendErrorMessage(err.response?.data?.detail));
      } else {
        setFailureKind("network");
        setError(err instanceof Error ? err.message : "Could not remove draft evidence.");
      }
    } finally {
      setUploadingDraftRequirement(null);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <section>
          <h1 className="text-2xl font-semibold text-slate-950">
            {profile ? `Welcome, ${profile.name}` : "Applicant Dashboard"}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Track your scholarship application, documents, corrections, and payment status.
          </p>
        </section>

        {error ? (
          <div
            role="alert"
            className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          >
            <p className="font-medium">
              {failureKind === "validation"
                ? "Submission needs correction"
                : failureKind === "network"
                  ? "Connection problem"
                  : failureKind === "server"
                    ? "Submission was not saved"
                    : "Attention needed"}
            </p>
            <p className="mt-1">{error}</p>
          </div>
        ) : null}
        {submissionSuccess ? (
          <div
            role="status"
            className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
          >
            {submissionSuccess}
          </div>
        ) : null}
        {!isOnline ? (
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            You appear offline. Your draft stays in this browser session; submit after reconnecting.
          </div>
        ) : null}
        {loading ? <div className="rounded border bg-white p-4 text-sm">Loading...</div> : null}

        {readiness && !readiness.can_accept_real_submissions ? (
          <section className="rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            <h2 className="font-semibold">External applicant intake is not open</h2>
            <p className="mt-1">
              This applicant workspace is available for controlled readiness testing only.
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {readiness.stop_reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="rounded border bg-white">
            <div className="border-b p-4">
              <h2 className="font-semibold text-slate-950">My Applications</h2>
            </div>
            <div className="divide-y">
              {submissions.length === 0 ? (
                <div className="p-4 text-sm text-slate-600">No applications submitted yet.</div>
              ) : (
                submissions.map((submission) => (
                  <article key={submission.id} className="p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h3 className="font-medium text-slate-950">
                          Application #{submission.id}
                        </h3>
                        <p className="text-sm text-slate-600">
                          Program {submission.program_code || "pending"} - {submission.academic_year}
                        </p>
                      </div>
                      <span className="rounded bg-indigo-50 px-3 py-1 text-sm font-medium text-indigo-800">
                        {applicantStatusLabel(submission.status)}
                      </span>
                    </div>
                    {submission.status === "resubmission_required" ? (
                      <p className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        Action needed: update the application form and resubmit only once your corrections are ready.
                      </p>
                    ) : null}
                    {(() => {
                      const state = applicationStates.find(
                        (item) => item.submission?.id === submission.id
                      );
                      const submittedValues = state?.submission?.submitted_data ?? submission.data;
                      const visibleEntries = Object.entries(submittedValues).filter(
                        ([name]) => !name.startsWith("_")
                      );
                      return visibleEntries.length > 0 ? (
                        <div className="mt-4 rounded border border-slate-200 bg-white p-3">
                          <h4 className="text-sm font-semibold text-slate-900">
                            Submitted application data
                          </h4>
                          <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                            {visibleEntries.map(([name, value]) => (
                              <div key={name}>
                                <dt className="text-xs font-medium uppercase text-slate-500">
                                  {fieldLabel(name, state?.form.schema.properties?.[name])}
                                </dt>
                                <dd className="mt-1 break-words text-slate-900">
                                  {formatSubmittedValue(value)}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                      ) : null;
                    })()}
                    {documentsBySubmission[submission.id] ? (
                      <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h4 className="text-sm font-semibold text-slate-900">
                            Documents
                          </h4>
                          <span
                            className={`rounded px-2 py-1 text-xs font-medium ${
                              documentsBySubmission[submission.id].complete
                                ? "bg-emerald-50 text-emerald-800"
                                : "bg-amber-50 text-amber-900"
                            }`}
                          >
                            {documentsBySubmission[submission.id].complete
                              ? "Complete"
                              : "Incomplete"}
                          </span>
                        </div>
                        <EvidenceRequirementsPanel
                          requirements={
                            documentsBySubmission[submission.id].evidence_requirements ?? []
                          }
                          title="Submitted evidence requirements"
                        />
                        <div className="mt-3 space-y-2">
                          {documentsBySubmission[submission.id].requirements.map((requirement) => (
                            <div
                              key={requirement.document_type}
                              className="flex items-center justify-between gap-3 text-sm"
                            >
                              <span className="text-slate-700">{requirement.label}</span>
                              <span
                                className={
                                  requirement.uploaded
                                    ? "font-medium text-emerald-700"
                                    : "font-medium text-amber-800"
                                }
                              >
                                {requirement.uploaded ? "Uploaded" : "Needed"}
                              </span>
                            </div>
                          ))}
                        </div>
                        {documentsBySubmission[submission.id].requirements.length > 0 ? (
                          <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr]">
                            <select
                              value={uploadTypeBySubmission[submission.id] ?? ""}
                              onChange={(event) =>
                                setUploadTypeBySubmission((current) => ({
                                  ...current,
                                  [submission.id]: event.target.value,
                                }))
                              }
                              className="rounded border px-3 py-2 text-sm"
                            >
                              {documentsBySubmission[submission.id].requirements.map((item) => (
                                <option key={item.document_type} value={item.document_type}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                            <input
                              type="file"
                              accept="application/pdf,image/jpeg,image/png"
                              disabled={uploadingDocument === submission.id || !isOnline}
                              onChange={(event) => {
                                void uploadDocument(
                                  submission.id,
                                  event.currentTarget.files?.[0] ?? null
                                );
                                event.currentTarget.value = "";
                              }}
                              className="rounded border px-3 py-2 text-sm"
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </article>
                ))
              )}
            </div>
          </div>

          <form onSubmit={submitApplication} className="rounded border bg-white p-4">
            <h2 className="font-semibold text-slate-950">{formHeading}</h2>
            <label className="mt-4 block text-sm font-medium text-slate-800">
              Program form
              <select
                value={formId}
                onChange={(event) => {
                  setFormId(event.target.value);
                  const nextForm = forms.find(
                    (form) => String(form.form_version_id) === event.target.value
                  );
                  if (nextForm) {
                    setAcademicYear(String(nextForm.academic_year));
                  }
                  setFieldValues({});
                  setFieldErrors({});
                  setSubmissionSuccess(null);
                  setError(null);
                  setFailureKind(null);
                }}
                className="mt-1 w-full rounded border px-3 py-2"
                required
              >
                {forms.length === 0 ? (
                  <option value="">No active forms</option>
                ) : (
                  forms.map((form) => (
                    <option key={form.active_form_version_id} value={form.form_version_id}>
                      {form.name}
                    </option>
                  ))
                )}
              </select>
            </label>
            {selectedForm?.description ? (
              <p className="mt-2 text-xs text-slate-600">{selectedForm.description}</p>
            ) : null}
            {selectedApplicationState ? (
              <div
                className={`mt-4 rounded border p-3 text-sm ${
                  selectedApplicationState.editable
                    ? "border-indigo-200 bg-indigo-50 text-indigo-950"
                    : "border-emerald-200 bg-emerald-50 text-emerald-900"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold">{selectedApplicationState.lifecycle_label}</p>
                  <span className="rounded bg-white px-2 py-1 text-xs font-medium">
                    {selectedApplicationState.existing_application
                      ? "Existing application"
                      : "No application yet"}
                  </span>
                </div>
                <p className="mt-2">{selectedApplicationState.cognition_message}</p>
                <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                  <div>
                    <dt className="font-medium uppercase opacity-70">Saved draft</dt>
                    <dd>
                      {hasSavedDraft
                        ? `Saved ${new Date(selectedApplicationState.draft?.updated_at ?? "").toLocaleString()}`
                        : selectedApplicationState.draft_supported
                          ? "No saved draft yet"
                          : "Draft is not available for this application"}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium uppercase opacity-70">Can I submit again?</dt>
                    <dd>{selectedApplicationState.repeat_submit_behavior.replaceAll("_", " ")}</dd>
                  </div>
                </dl>
              </div>
            ) : null}
            <EvidenceRequirementsPanel
              requirements={evidenceRequirements}
              editable={Boolean(isFormEditable && !resubmissionTarget)}
              disabled={!isOnline || savingDraft || submitting || uploadingDraftRequirement !== null}
              uploadTypeByRequirement={uploadTypeByRequirement}
              uploadingRequirement={uploadingDraftRequirement}
              onSelectDocumentType={(requirementKey, documentType) =>
                setUploadTypeByRequirement((current) => ({
                  ...current,
                  [requirementKey]: documentType,
                }))
              }
              onUploadDocument={(requirement, file) => void uploadDraftEvidence(requirement, file)}
              onRemoveDocument={(requirement, documentId) =>
                void removeDraftEvidence(requirement, documentId)
              }
            />
            {hasUnsavedBrowserChanges ? (
              <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Unsaved browser changes are present. Save a draft to make them resumable
                later, or submit the final application when ready.
              </div>
            ) : null}
            {submittedEntries.length > 0 ? (
              <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
                <h3 className="text-sm font-semibold text-slate-900">
                  Submitted application details
                </h3>
                <dl className="mt-3 grid gap-3 text-sm">
                  {submittedEntries.map(([name, value]) => (
                    <div key={name}>
                      <dt className="text-xs font-medium uppercase text-slate-500">
                        {fieldLabel(name, properties[name])}
                      </dt>
                      <dd className="mt-1 break-words text-slate-900">
                        {formatSubmittedValue(value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}
            <label className="mt-4 block text-sm font-medium text-slate-800">
              Academic year
              <input
                value={academicYear}
                onChange={(event) => setAcademicYear(event.target.value)}
                className="mt-1 w-full rounded border px-3 py-2"
                inputMode="numeric"
                disabled={!isFormEditable}
                required
              />
            </label>
            {fieldEntries.length === 0 ? (
              <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                This form is not publication-ready for applicant entry.
              </div>
            ) : (
              fieldEntries.map(([name, schema]) => (
                <label key={name} className="mt-4 block text-sm font-medium text-slate-800">
                  {schema.title || name.replaceAll("_", " ")}
                  <input
                    value={
                      isFormEditable
                        ? effectiveFieldValues[name] ?? ""
                        : formatSubmittedValue(submittedData[name])
                    }
                    onChange={(event) => updateFieldValue(name, schema, event.target.value)}
                    disabled={!isFormEditable}
                    className={`mt-1 w-full rounded border px-3 py-2 ${
                      fieldErrors[name]
                        ? "border-red-400 bg-red-50 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200"
                        : "border-slate-300"
                    }`}
                    required={required.has(name)}
                    inputMode={
                      schema.type === "integer" ||
                      schema.type === "number" ||
                      canonicalFieldKey(name, schema) === "aadhaar_number"
                        ? "numeric"
                        : "text"
                    }
                    aria-invalid={fieldErrors[name] ? "true" : "false"}
                    aria-describedby={fieldErrors[name] ? `${name}-error` : undefined}
                  />
                  {fieldErrors[name] ? (
                    <p id={`${name}-error`} className="mt-1 text-xs font-medium text-red-700">
                      {fieldErrors[name]}
                    </p>
                  ) : null}
                </label>
              ))
            )}
            <button
              type="button"
              onClick={() => void saveDraft()}
              disabled={
                !selectedForm ||
                !isFormEditable ||
                Boolean(resubmissionTarget) ||
                savingDraft ||
                submitting ||
                !isOnline
              }
              className="mt-4 w-full rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-900 disabled:opacity-50"
            >
              {savingDraft ? "Saving draft..." : hasSavedDraft ? "Save draft changes" : "Save draft"}
            </button>
            <button
              type="submit"
              disabled={
                !selectedForm ||
                fieldEntries.length === 0 ||
                submitting ||
                savingDraft ||
                !isOnline ||
                !isFormEditable
              }
              className="mt-3 w-full rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {submitting
                ? "Submitting..."
                : resubmissionTarget
                  ? "Submit corrections"
                  : selectedApplicationState?.submit_button_label || "Submit final application"}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
};

export default ApplicantDashboard;
